// Wave 20.43 — Aero Wake landing screen.
// Wave 20.44 — real data + user journey.
// Wave 20.49cm — chrome moved OUT of the wake's way.
//
// Full-bleed host for the /wake-embed canvas animation. States:
//
//   1. Zero tested positions: the embed renders the rider's STARTING
//      wake (4 core defaults, greyed) with a bottom "starting wake"
//      banner — never an empty page, never demo fixtures.
//   2. Populated: derive real per-position URL params from
//      /sessions/me/positions/analytics, pass to the embed.
//   3. Loading / paywall: overlays hide until we know.
//
// Layout contract with the embed: everything that overlays the canvas
// lives in exactly two measured containers — TOP chrome (identity
// strip + "Add position" pill) and BOTTOM chrome (AERO AI card, then
// any banner). A ResizeObserver posts their extents into the iframe as
// { type: 'aero-wake:insets', top, bottom } and the embed maps every
// stream + label into the remaining band. The AI card used to sit
// absolutely at the top — directly over the origin bundle where all
// streams start — while the bottom of the canvas below STANDING was
// empty. Now the card takes that dead space and the canvas lays out
// around it, regardless of how tall the card's text wraps.
//
// Interactive: the embed emits { type: 'aero-wake:focus', class }
// postMessages when the rider taps a position label (measured OR
// default). This host listens and navigates to
// /session/focus?preselect=<class>.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import { colors } from './theme';
import { usePositionsAnalytics } from './hooks/usePositionsAnalytics';
import { useSubscription } from './services/useSubscription';
import WakeAi from './components/WakeAi';

// Shared card chrome for the bottom banners (free-tier + starting wake).
const bannerSx = {
  width: '100%',
  p: 2,
  borderRadius: 3,
  backgroundColor: 'rgba(10, 14, 26, 0.85)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${colors.borderFocus}`,
  boxShadow: `0 24px 56px rgba(0, 0, 0, 0.55)`,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flexWrap: 'wrap' as const,
};

export default function Wake() {
  const nav = useNavigate();
  const { data, loading, error } = usePositionsAnalytics();
  // Wave 20.44 — free tier is capped at 1 position. usage.maxPositions
  // is null for Pro (unlimited) and 1 for free. When free + already
  // has 1 position → show the upgrade banner and gate "Add position".
  const { usage } = useSubscription();
  const maxPositions = usage?.maxPositions ?? null;

  const positions = data?.positions ?? [];
  const totalSessions = data?.totalSessions ?? 0;
  const showEmptyState = !loading && !error && positions.length === 0;
  const atPositionCap =
    maxPositions != null && positions.length >= maxPositions;

  const hostRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const topChromeRef = useRef<HTMLDivElement | null>(null);
  const bottomChromeRef = useRef<HTMLDivElement | null>(null);

  // Build the ?avgCda=…&positions=… params — passed to /wake-embed
  // whenever we have real data.
  const embedSrc = useMemo(() => {
    // Wave 20.49cl — zero tested positions is the rider's INITIAL
    // wake, not an empty page: the embed seeds the 4 core defaults
    // (greyed, tagged DEFAULT). `noDemo=1` tells it this is a real
    // rider so it must not fill the marketing demo fixtures.
    if (!positions.length) return '/wake-embed?noDemo=1';
    const meanCda =
      positions.reduce((sum, p) => sum + p.meanCda, 0) / positions.length;
    // URL contract: class:meanCda:variance:sessionCount. Use
    // worstCda - meanCda as the variance proxy; that's the spread the
    // embed uses to drive per-stream turbulence.
    const positionsParam = positions
      .map((p) => {
        const variance = Math.max(0, p.worstCda - p.meanCda);
        // Wave 20.49n — 6th token: per-measurement cda|variance
        // joined by ';'. WakeEmbed spawns one stream per entry
        // anchored at that measurement's own CdA with its own
        // waviness. Falls back to sessionCount×meanCda when
        // absent (older API responses).
        const measurements = (p.recentBests ?? [])
          .map((r) => {
            const rMean = r.cdaMean ?? r.bestCda;
            const rVar = Math.max(0, rMean - r.bestCda);
            return `${r.bestCda.toFixed(3)}|${rVar.toFixed(3)}`;
          })
          .join(';');
        return [
          p.class,
          p.meanCda.toFixed(3),
          variance.toFixed(3),
          p.sessionCount,
          encodeURIComponent(p.alias ?? ''),
          measurements,
        ].join(':');
      })
      .join(',');
    const q = new URLSearchParams({
      avgCda: meanCda.toFixed(3),
      positions: positionsParam,
    });
    return `/wake-embed?${q.toString()}`;
  }, [positions]);

  // Wave 20.49cm — measure the overlay chrome and tell the embed how
  // much of the canvas it covers at the top and bottom.
  const postInsets = useCallback(() => {
    const host = hostRef.current;
    const win = iframeRef.current?.contentWindow;
    if (!host || !win) return;
    const hostRect = host.getBoundingClientRect();
    const topRect = topChromeRef.current?.getBoundingClientRect();
    const bottomRect = bottomChromeRef.current?.getBoundingClientRect();
    const top = topRect ? Math.max(0, topRect.bottom - hostRect.top) : 0;
    const bottom = bottomRect ? Math.max(0, hostRect.bottom - bottomRect.top) : 0;
    win.postMessage({ type: 'aero-wake:insets', top, bottom }, '*');
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => postInsets());
    if (hostRef.current) ro.observe(hostRef.current);
    if (topChromeRef.current) ro.observe(topChromeRef.current);
    if (bottomChromeRef.current) ro.observe(bottomChromeRef.current);
    postInsets();
    return () => ro.disconnect();
    // The chrome containers mount/unmount with loading + banner
    // state; re-observe when those flip.
  }, [postInsets, loading, showEmptyState, atPositionCap]);

  // Listen for label-tap events from the embed.
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const data = ev.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'aero-wake:focus') return;
      const cls = typeof data.class === 'string' ? data.class : null;
      if (!cls) return;
      if (cls === 'new') {
        nav('/session/focus');
      } else {
        nav(`/session/focus?preselect=${encodeURIComponent(cls)}`);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [nav]);

  return (
    <PageLayout fullBleed>
      <Box
        ref={hostRef}
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          backgroundColor: colors.bg,
          // Wave 20.44 — establish a stacking context BELOW the
          // AppBar (z-index 1100). Without this, the "Add position"
          // pill's backdrop-filter creates its own context that can
          // hop above the sticky header on some browsers.
          zIndex: 0,
        }}
      >
        <iframe
          ref={iframeRef}
          src={embedSrc}
          title="Aero Wake"
          onLoad={postInsets}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />

        {/* TOP chrome — identity strip (left) + Add-position pill
            (right). One measured container so the embed can keep
            streams below it. */}
        <Box
          ref={topChromeRef}
          sx={{
            position: 'absolute',
            top: { xs: 88, md: 104 },
            left: { xs: 20, md: 32 },
            right: { xs: 20, md: 32 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ pointerEvents: 'none' }}>
            <Typography
              sx={{
                color: colors.brand,
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.22em',
              }}
            >
              AERO WAKE
            </Typography>
            {!loading && (
              <Typography
                sx={{
                  color: colors.textMuted,
                  fontSize: '0.72rem',
                  mt: 0.5,
                  fontFamily: '"JetBrains Mono", monospace',
                  letterSpacing: '0.08em',
                }}
              >
                {showEmptyState
                  ? 'Starting wake · 4 defaults'
                  : `${positions.length} position${positions.length === 1 ? '' : 's'} · ${totalSessions} session${totalSessions === 1 ? '' : 's'}`}
              </Typography>
            )}
          </Box>

          {/* Wave 20.47h -> 20.48b: this is the DOM pill the rider
              asked me to KEEP. Canvas-painted pill inside WakeEmbed
              stays removed. */}
          {!loading && (
            <Button
              onClick={() => nav(atPositionCap ? '/subscription' : '/session/focus')}
              startIcon={atPositionCap ? <LockIcon /> : <AddIcon />}
              sx={{
                pointerEvents: 'auto',
                flexShrink: 0,
                px: 2,
                py: 1,
                borderRadius: 999,
                backgroundColor: 'rgba(10, 14, 26, 0.8)',
                backdropFilter: 'blur(12px)',
                border: `1px solid ${colors.borderFocus}`,
                color: colors.brand,
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                '&:hover': {
                  backgroundColor: 'rgba(18, 24, 38, 0.9)',
                  borderColor: colors.brand,
                },
              }}
            >
              {atPositionCap ? 'Unlock more' : 'Add position'}
            </Button>
          )}
        </Box>

        {/* BOTTOM chrome — AERO AI card, then any banner, stacked.
            Sits in the dead space below the wake; the embed reserves
            this band so nothing renders underneath. */}
        <Box
          ref={bottomChromeRef}
          sx={{
            position: 'absolute',
            left: { xs: 16, md: 32 },
            right: { xs: 16, md: 32 },
            bottom: { xs: 20, md: 32 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
            zIndex: 5,
          }}
        >
          {/* Wave 20.49g — AERO AI nudge card: contextual sentence
              (weather + rider's real position aliases) and up to 4
              action buttons. */}
          <WakeAi />

          {/* Wave 20.49cl — First-run banner. The rider's starting
              wake (4 greyed defaults) stays fully visible above. */}
          {showEmptyState && (
            <Box sx={bannerSx}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography
                  sx={{
                    color: colors.brand,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    letterSpacing: '0.16em',
                  }}
                >
                  YOUR STARTING WAKE
                </Typography>
                <Typography sx={{ color: colors.text, fontSize: '0.95rem', fontWeight: 700, mt: 0.25 }}>
                  Four default positions, estimated. Tap one to measure it and make it yours.
                </Typography>
              </Box>
              <Button
                onClick={() => nav('/session/focus')}
                startIcon={<AddIcon />}
                variant="contained"
                sx={{
                  backgroundColor: colors.brand,
                  color: colors.bg,
                  fontWeight: 800,
                  px: 3,
                  py: 1.25,
                  letterSpacing: '0.04em',
                  '&:hover': { backgroundColor: colors.brandBright },
                }}
              >
                Measure a position
              </Button>
            </Box>
          )}

          {/* Wave 20.44 — free-tier upgrade banner. Only when they've
              hit the position cap; hidden in the empty state / Pro. */}
          {atPositionCap && !showEmptyState && !loading && (
            <Box sx={bannerSx}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${colors.brand}18`,
                  border: `1px solid ${colors.brand}66`,
                  color: colors.brand,
                }}
              >
                <LockIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography
                  sx={{
                    color: colors.brand,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    letterSpacing: '0.16em',
                  }}
                >
                  FREE PLAN
                </Typography>
                <Typography sx={{ color: colors.text, fontSize: '0.95rem', fontWeight: 700, mt: 0.25 }}>
                  One position, one stream. Upgrade to measure every position you ride.
                </Typography>
              </Box>
              <Button
                onClick={() => nav('/subscription')}
                variant="contained"
                sx={{
                  backgroundColor: colors.brand,
                  color: colors.bg,
                  fontWeight: 800,
                  px: 3,
                  py: 1.25,
                  letterSpacing: '0.04em',
                  '&:hover': { backgroundColor: colors.brandBright },
                }}
              >
                Upgrade
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}
